import { Tooltip } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

const STATUS_STYLES = {
  Likely: "border-success-200 bg-[#ECFDF3] text-success",
  Unlikely: "border-warning-200 bg-[#FEF0C7] text-warning",
};

// Status pill ("Likely"/"Unlikely") with an explanation that opens on hover,
// keyboard focus and tap. MUI Tooltip closes on Esc and links the text via aria-describedby.
const StatusPill = ({ status, tooltipTitle, tooltipBody }) => (
  <Tooltip
    describeChild
    arrow={false}
    enterTouchDelay={0}
    leaveTouchDelay={6000}
    placement="bottom-start"
    title={
      <div className="p-1">
        <p className="text-sm font-semibold text-gray-900">{tooltipTitle}</p>
        <p className="text-sm text-gray-600 mt-1 leading-snug">{tooltipBody}</p>
      </div>
    }
    componentsProps={{
      tooltip: {
        sx: {
          bgcolor: "#fff",
          fontFamily: "inherit",
          color: "inherit",
          maxWidth: 320,
          border: "1px solid #D0D5DD",
          borderRadius: "12px",
          boxShadow: "0 12px 16px -4px rgba(16,24,40,0.08)",
          padding: "12px",
        },
      },
    }}
  >
    <button
      type="button"
      className="flex items-center gap-1 rounded-full border border-gray-300 bg-white pl-1 pr-1.5 py-0.5 shrink-0 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-500"
    >
      <span
        className={`text-xs font-medium px-2 py-0.5 rounded-full border ${STATUS_STYLES[status] ?? STATUS_STYLES.Likely}`}
      >
        {status}
      </span>
      <InfoOutlinedIcon
        aria-hidden="true"
        sx={{ fontSize: 16 }}
        className="text-gray-500"
      />
      <span className="sr-only">, what this means</span>
    </button>
  </Tooltip>
);

// Faint graph-paper grid behind the card content
const GRID_BACKGROUND = {
  backgroundImage:
    "linear-gradient(to right, rgba(16,24,40,0.05) 1px, transparent 1px), linear-gradient(to bottom, rgba(16,24,40,0.05) 1px, transparent 1px)",
  backgroundSize: "20px 20px",
};

// One SAI figure (Federal or Institutional) with its source, impact line and status
const SaiCard = ({
  title,
  source,
  value,
  impact,
  status,
  tooltipTitle,
  tooltipBody,
}) => {
  return (
    <article
      aria-label={title}
      style={GRID_BACKGROUND}
      className="flex flex-col bg-white rounded-xl border border-gray-200 border-l-4 border-l-primary-500 shadow-sm p-4 min-w-0"
    >
      <div className="flex items-start justify-between gap-2">
        <h3 className="m-0 text-lg font-medium text-gray-900">{title}</h3>
        <span className="text-xs text-gray-500 pt-1">{source}</span>
      </div>
      <p className="text-3xl xl:text-[2.25rem] leading-tight font-medium text-primary-500 mt-2 break-words">
        {value}
      </p>
      <div className="flex items-end justify-between gap-2 mt-3">
        <p className="text-sm text-gray-700 leading-snug">{impact}</p>
        {status && (
          <StatusPill
            status={status}
            tooltipTitle={tooltipTitle}
            tooltipBody={tooltipBody}
          />
        )}
      </div>
    </article>
  );
};

export default SaiCard;
